import axios from "axios";
import CryptoJS from "crypto-js";
import {
  BASE_URL, ENDPOINTS,
  ORDER_LINK_BASE_URL,
  PAYMENT_LINK_PROD_DOMAIN,
  LOCALHOST_PAYMENT_BASE,
} from "../constants/api";
import { msgTypes } from "../constants/msgTypes";

/**
 * Encrypts any JS object using AES-256-CBC + PBKDF2.
 * Returns a Base64 string: IV (16 bytes) prepended to ciphertext.
 */
const encrypt = (payload) => {
  const jsonStr = JSON.stringify(payload);
  const iv = CryptoJS.lib.WordArray.random(16);
  const key = CryptoJS.PBKDF2(msgTypes.APP_ID, msgTypes.SALT_ID, {
    keySize: msgTypes.KEY_LENGTH / 32,
    iterations: msgTypes.ITERATION_COUNT,
  });
  const encrypted = CryptoJS.AES.encrypt(jsonStr, key, {
    iv,
    padding: CryptoJS.pad.Pkcs7,
    mode: CryptoJS.mode.CBC,
  });
  return iv.concat(encrypted.ciphertext).toString(CryptoJS.enc.Base64);
};

/**
 * Encrypts payload, wraps it with appId, POSTs to endpoint.
 * All API calls go through this to ensure consistent encryption.
 */
const securePost = async (endpoint, payload) => {
  const data = encrypt(payload);
  const res = await axios.post(
    BASE_URL + endpoint,
    { appId: "", data },
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

/**
 * Sends `data` string directly without encryption.
 * Used for Flow B: the segment after /pay/ is already the encoded payload.
 */
const rawPost = async (endpoint, data) => {
  const res = await axios.post(
    BASE_URL + endpoint,
    { appId: "", data },
    { headers: { "Content-Type": "application/json" } }
  );
  return res.data;
};

/**
 * payReq-specific encryption (matches Java reference implementation).
 * PBKDF2 password = CHECKOUT_ID, salt = CHECKOUT_KEY
 * Output: Base64( IV[16 bytes] + AES-CBC ciphertext )
 */
const encryptPayReq = (payload) => {
  const jsonStr = JSON.stringify(payload);
  const iv  = CryptoJS.lib.WordArray.random(16);
  const key = CryptoJS.PBKDF2(msgTypes.CHECKOUT_ID, msgTypes.CHECKOUT_KEY, {
    keySize:    msgTypes.KEY_LENGTH / 32,      // 256-bit key
    iterations: msgTypes.ITERATION_COUNT,       // 65536
  });
  const encrypted = CryptoJS.AES.encrypt(jsonStr, key, {
    iv,
    padding: CryptoJS.pad.Pkcs7,
    mode:    CryptoJS.mode.CBC,
  });
  return iv.concat(encrypted.ciphertext).toString(CryptoJS.enc.Base64);
};

/**
 * Order-link encryption — mirrors Java OrderGenerateRequest.encrypt()
 *
 * PBKDF2 password = appId (form value, e.g. "UATAPP22F05M26D110840A")
 * PBKDF2 salt     = secretKey (msgTypes.ORDER_LINK_SECRET_KEY)
 * Output: Base64( IV[16 bytes] || AES-256-CBC ciphertext )
 */
const encryptOrderLink = (payload, appId, secretKey) => {
  const jsonStr = JSON.stringify(payload);
  const iv      = CryptoJS.lib.WordArray.random(16);
  const key     = CryptoJS.PBKDF2(appId, secretKey, {
    keySize:    msgTypes.KEY_LENGTH / 32,   // 256-bit key
    iterations: msgTypes.ITERATION_COUNT,   // 65536
  });
  const encrypted = CryptoJS.AES.encrypt(jsonStr, key, {
    iv,
    padding: CryptoJS.pad.Pkcs7,
    mode:    CryptoJS.mode.CBC,
  });
  return iv.concat(encrypted.ciphertext).toString(CryptoJS.enc.Base64);
};

/**
 * Returns true when the app is running on localhost (any port).
 */
const isLocalhost = () =>
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

/**
 * Rewrites the payment link for local development:
 *   https://payment.domain.com/<token>  →  http://localhost:<port>/<token>
 * In production the link is returned unchanged.
 */
const resolvePaymentLink = (paymentLink) => {
  if (!isLocalhost() || !paymentLink) return paymentLink;
  return paymentLink.replace(PAYMENT_LINK_PROD_DOMAIN, LOCALHOST_PAYMENT_BASE);
};

export const PaymentService = {
  /** Flow A (InitPage): full form data → encrypted → paymentDetails */
  getPaymentDetails: (payload) => securePost(ENDPOINTS.PAYMENT_DETAILS, payload),

  /** Flow B (CheckoutPage): raw URL segment sent as-is, no encryption */
  getPaymentDetailsRaw: (rawData) => rawPost(ENDPOINTS.PAYMENT_DETAILS, rawData),

  /** Submit payment request — uses payReq-specific encryption (CHECKOUT_ID / CHECKOUT_KEY) */
  submitPayReq: async (payload) => {
    const data = encryptPayReq(payload);
    const res = await axios.post(
      BASE_URL + ENDPOINTS.PAY_REQ,
      { appId: "", data },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data;
  },

  /** Verify a UPI VPA — plain JSON, no encryption */
  verifyVPA: async (vpa) => {
    const res = await axios.post(
      BASE_URL + ENDPOINTS.VERIFY_VPA,
      { vpa },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data;
  },

  /** Poll payment status — GET /paymentStatus/:transactionId */
  getPaymentStatus: async (transactionId) => {
    const res = await axios.get(
      `${BASE_URL}${ENDPOINTS.PAYMENT_STATUS}/${transactionId}`
    );
    return res.data;
  },

  /**
   * Generate a hosted payment link — mirrors Java OrderGenerateRequest.
   *
   * Encrypts the order payload with PBKDF2(appId, ORDER_LINK_SECRET_KEY),
   * POSTs to /api/v2/req/orderRequest, then rewrites the returned
   * paymentLink domain to localhost when running locally.
   *
   * @param {object} formValues  – values from InitPage form
   * @returns {object}           – API response with `data.paymentLink` resolved
   */
  generateOrderLink: async (formValues) => {
    const {
      appId, secretKey, orderId, amount, custEmail, custPhone,
      custFirstName, currencyCode, transactionType,
      productDesc, returnUrl, userIpAddress,
    } = formValues;

    const payload = {
      appId,
      orderId,
      amount,
      transactionType,
      currencyCode,
      custEmail,
      custPhone,
      custFirstName,
      returnUrl:      returnUrl       || "",
      userIpAddress:  userIpAddress   || "179.1.44.89",
      productDesc,
    };

    const encryptedData = encryptOrderLink(payload, appId, secretKey);

    const res = await axios.post(
      ORDER_LINK_BASE_URL + ENDPOINTS.ORDER_REQUEST,
      { appId, data: encryptedData },
      { headers: { "Content-Type": "application/json" } }
    );

    const responseBody = res.data;

    // Rewrite paymentLink domain to localhost when developing locally
    // API returns paymentLink at the top level of the response body
    if (responseBody?.paymentLink) {
      responseBody.paymentLink = resolvePaymentLink(responseBody.paymentLink);
    }

    return responseBody;
  },
};
